import { ArrowDown, ArrowUp } from "lucide-react";

import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import { useGetReplayBaseAnalytics } from "../../api/replay/get-replay-base-analytics";

type ReplayBaseAnalyticsProps = {
  organisationId?: string;
};

export function ReplayBaseAnalytics({
  organisationId,
}: ReplayBaseAnalyticsProps) {
  const { data, isLoading, isSuccess, isError, error } =
    useGetReplayBaseAnalytics(organisationId);

  if (isLoading) {
    return (
      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <p className="mb-5 text-sm font-semibold text-foreground">
              Average Replay Length
            </p>

            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <p className="text-sm font-semibold text-foreground">
              Number of Replays
            </p>
            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <p className="text-sm font-semibold text-foreground">
              Average Number of Views
            </p>
            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
      </section>
    );
  }

  if (isError || !isSuccess) {
    return <ErrorMessage error={error} />;
  }

  const { avgViews, replayCount, avgReplayLength } = data;
  const isPositiveAvgViews = avgViews.current > avgViews.previous;
  const avgViewsChange = avgViews.previous
    ? Math.round(
        ((avgViews.current - avgViews.previous) / avgViews.previous) * 100,
      )
    : 0;

  const isPositiveReplayCount = replayCount.current > replayCount.previous;
  const replayCountChange = replayCount.previous
    ? Math.round(
        ((replayCount.current - replayCount.previous) / replayCount.previous) *
          100,
      )
    : 0;

  const isPositiveAvgReplayLength =
    avgReplayLength.current > avgReplayLength.previous;
  const avgReplayLengthChange = avgReplayLength.previous
    ? Math.round(
        ((avgReplayLength.current - avgReplayLength.previous) /
          avgReplayLength.previous) *
          100,
      )
    : 0;

  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <p className="mb-5 text-sm font-semibold text-foreground">
            Average Replay Length
          </p>

          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {Math.round((avgReplayLength.current ?? 0) / 60)} minutes
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge
                variant={isPositiveAvgReplayLength ? "success" : "destructive"}
              >
                {isPositiveAvgReplayLength ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveAvgReplayLength ? "+" : ""}
                  {avgReplayLengthChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <p className="text-sm font-semibold text-foreground">
            Number of Replays
          </p>
          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {replayCount.current ?? 0}
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge
                variant={isPositiveReplayCount ? "success" : "destructive"}
              >
                {isPositiveReplayCount ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveReplayCount ? "+" : ""}
                  {replayCountChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <p className="text-sm font-semibold text-foreground">
            Average Number of Views
          </p>

          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {avgViews.current ?? 0}
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge variant={isPositiveAvgViews ? "success" : "destructive"}>
                {isPositiveAvgViews ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveAvgViews ? "+" : ""}
                  {avgViewsChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
    </section>
  );
}
